/**
 * Base Provider - Common implementation for RTC providers.
 *
 * This class provides common functionality that can be shared
 * across different provider implementations.
 *
 * @internal
 * @packageDocumentation
 */

import type {
  RTCProviderEvents,
  AnimationTrackCallbacks,
  AudioTrackCallbacks,
} from '../../core/types';
import type { RTCProviderEventHandler } from '../../core/RTCProvider';
import { logger } from '../../utils';

/**
 * Base class for RTC providers.
 * Provides common event handling and state management.
 */
export abstract class BaseProvider {
  /** Provider name identifier */
  abstract readonly name: string;

  /** @internal */
  protected connectionState: string = 'disconnected';
  /** @internal */
  protected eventHandlers: Map<string, Set<RTCProviderEventHandler>> =
    new Map();

  /**
   * Pre-warm a future RTC connection if the provider supports it.
   */
  prepareConnection?(
    config: import('../../types').RTCPrepareConnectionConfig,
  ): Promise<void>;

  /**
   * Connect to RTC server.
   * @param config - Connection configuration
   */
  abstract connect(
    config: import('../../types').RTCConnectionConfig,
  ): Promise<void>;

  /**
   * Disconnect from RTC server.
   */
  abstract disconnect(): Promise<void>;

  /**
   * Get current connection state.
   */
  abstract getConnectionState(): string;

  /**
   * Subscribe to animation track.
   * @param callbacks - Animation track callbacks
   * @internal
   */
  abstract subscribeAnimationTrack(
    callbacks: AnimationTrackCallbacks,
  ): Promise<void>;

  /**
   * Unsubscribe from animation track.
   * @internal
   */
  abstract unsubscribeAnimationTrack(): Promise<void>;

  /**
   * Subscribe to audio track.
   * @param callbacks - Audio track callbacks
   * @internal
   */
  abstract subscribeAudioTrack(callbacks: AudioTrackCallbacks): Promise<void>;

  /**
   * Unsubscribe from audio track.
   * @internal
   */
  abstract unsubscribeAudioTrack(): Promise<void>;

  /**
   * Publish local audio track.
   * @param track - Optional MediaStreamTrack to publish
   */
  abstract publishAudioTrack(track?: MediaStreamTrack): Promise<void>;

  /**
   * Unpublish audio track.
   */
  abstract unpublishAudioTrack(): Promise<void>;

  /**
   * Get the native RTC client object.
   * @returns The native client, or null if not connected
   */
  abstract getNativeClient(): unknown;

  /**
   * Add event listener.
   * @param event - Event name
   * @param handler - Event handler
   */
  on(event: string, handler: RTCProviderEventHandler): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    this.eventHandlers.get(event)!.add(handler);
  }

  /**
   * Remove event listener.
   * @param event - Event name
   * @param handler - Event handler
   */
  off(event: string, handler: RTCProviderEventHandler): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.delete(handler);
    }
  }

  /**
   * Emit event to all listeners.
   * @param event - Event name
   * @param args - Event arguments
   * @internal
   */
  protected emit<K extends keyof RTCProviderEvents>(
    event: K,
    ...args: Parameters<RTCProviderEvents[K]>
  ): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (handler as any)(...args);
        } catch (error) {
          logger.error(
            this.name,
            `Error in event handler for ${event}:`,
            error,
          );
        }
      });
    }
  }

  /**
   * Update connection state and emit event.
   * @param state - New connection state
   * @internal
   */
  protected setConnectionState(state: string): void {
    if (this.connectionState !== state) {
      const prevState = this.connectionState;
      this.connectionState = state;

      // Log connection state changes
      if (state === 'disconnected' || state === 'failed') {
        logger.error(this.name, `Connection state: ${prevState} -> ${state}`);
      } else if (state === 'reconnecting') {
        logger.warn(this.name, `Connection state: ${prevState} -> ${state}`);
      } else {
        logger.info(this.name, `Connection state: ${prevState} -> ${state}`);
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.emit('connection-state-changed', state as any);
    }
  }
}
